// src/lib/stripeRpc.js
import { getAuth } from "firebase/auth";

/**
 * You can override this for local emulation:
 *   VITE_FUNCTIONS_BASE_URL="http://localhost:5001/<project-id>/us-central1"
 * If unset, we call same-origin (assumes a Hosting rewrite to /stripe/**).
 */
const BASE = (import.meta?.env?.VITE_FUNCTIONS_BASE_URL || "").replace(/\/+$/, "");

/** Join base + path safely */
const urlJoin = (base, path) =>
  `${String(base || "").replace(/\/+$/, "")}/${String(path || "").replace(/^\/+/, "")}`;

const RPC_URL = urlJoin(BASE, "/stripe/rpc");

// Reasonable network guard (client UX only; server is authoritative)
const REQUEST_TIMEOUT_MS = 20000;

/** Get current user's Firebase ID token. Returns null if signed out. */
async function getIdToken(forceRefresh = false) {
  const auth = getAuth();
  const u = auth.currentUser || null;
  return u ? u.getIdToken(forceRefresh) : null;
}

/**
 * Low-level RPC caller.
 * - By default requires auth; pass { requireAuth:false } for public endpoints.
 *
 * @template T
 * @param {string} action
 * @param {Record<string, any>} params
 * @param {{ requireAuth?: boolean, timeoutMs?: number, signal?: AbortSignal }} [opts]
 * @returns {Promise<T>}
 */
async function rpc(action, params = {}, opts = {}) {
  const { requireAuth = true, timeoutMs = REQUEST_TIMEOUT_MS, signal } = opts;

  const idToken = await getIdToken();
  if (requireAuth && !idToken) {
    const err = new Error("UNAUTHENTICATED");
    // @ts-ignore - handy for callers that branch on status
    err.status = 401;
    throw err;
  }

  const headers = { "Content-Type": "application/json" };
  if (idToken) headers["Authorization"] = `Bearer ${idToken}`;

  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), Math.max(1, timeoutMs));
  // Allow external signals to abort too
  if (signal) signal.addEventListener("abort", () => ctrl.abort(), { once: true });

  let res;
  try {
    res = await fetch(RPC_URL, {
      method: "POST",
      headers,
      body: JSON.stringify({ action, ...params }),
      credentials: "include",
      signal: ctrl.signal,
    });
  } finally {
    clearTimeout(t);
  }

  // Try to parse JSON either way (ok or error)
  let data = null;
  const text = await res.text().catch(() => "");
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    // fall through; we'll build an error below if needed
  }

  if (!res.ok || data?.ok === false) {
    const message = (data && (data.error || data.message)) || text || `HTTP ${res.status}`;
    const err = new Error(String(message));
    // @ts-ignore
    err.status = res.status;
    throw err;
  }

  return /** @type {T} */ (data);
}

/** ===================== Public helpers ===================== */

/**
 * Start a day-use checkout.
 * Server action: "startCheckoutStandard"
 * @param {{ listingId: string|number, date: string, guests?: number, note?: string }} args
 * @returns {Promise<{ ok: true, bookingId: string, sessionUrl: string, route: "connect_destination"|"platform_fallback" }>}
 */
export function startCheckoutStandard(args) {
  return rpc("startCheckoutStandard", args, { requireAuth: true });
}

/**
 * Start an overnight checkout.
 * Server action: "startCheckoutOvernight"
 * @param {{ listingId: string|number, startDate: string, endDateExclusive: string, guests?: number, note?: string }} args
 * @returns {Promise<{ ok: true, bookingId: string, sessionUrl: string, route: "connect_destination"|"platform_fallback" }>}
 */
export function startCheckoutOvernight(args) {
  return rpc("startCheckoutOvernight", args, { requireAuth: true });
}

/**
 * Approve a booking (captures the manual authorization).
 * Server action: "booking.approve"
 * @param {string} bookingId
 * @returns {Promise<{ ok: true }>}
 */
export function bookingApprove(bookingId) {
  return rpc("booking.approve", { bookingId }, { requireAuth: true });
}

/**
 * Decline a booking (cancels the auth + releases holds).
 * Server action: "booking.decline"
 * @param {string} bookingId
 * @returns {Promise<{ ok: true }>}
 */
export function bookingDecline(bookingId) {
  return rpc("booking.decline", { bookingId }, { requireAuth: true });
}

/**
 * Optional: Availability checks (public; allow signed-out UX).
 */
export function checkAvailability({ listingId, date }) {
  return rpc("checkAvailability", { listingId, date }, { requireAuth: false });
}
export function checkAvailabilityRange({ listingId, startDate, endDateExclusive }) {
  return rpc(
    "checkAvailabilityRange",
    { listingId, startDate, endDateExclusive },
    { requireAuth: false }
  );
}

/**
 * Optional: Host/Connect helpers you may want in settings.
 */
export function ensureExpressAccount() {
  return rpc("ensureExpressAccount", {}, { requireAuth: true });
}
export function createExpressOnboardingLink() {
  return rpc("createExpressOnboardingLink", {}, { requireAuth: true });
}
export function createExpressDashboardLink() {
  return rpc("createExpressDashboardLink", {}, { requireAuth: true });
}
export function hostGetAccount() {
  return rpc("host.getAccount", {}, { requireAuth: true });
}
export function hostGetOnboardingStatus() {
  return rpc("host.getOnboardingStatus", {}, { requireAuth: true });
}
export function hostListPayouts(params = {}) {
  return rpc("host.listPayouts", params, { requireAuth: true });
}
export function hostGetBalance() {
  return rpc("host.getBalance", {}, { requireAuth: true });
}

/**
 * Optional: Diagnostics
 */
export function diagListingHost(listingId) {
  return rpc("diag.listingHost", { listingId }, { requireAuth: true });
}

/**
 * Convenience: redirect the browser to Stripe Checkout.
 * Use the startCheckout* result to navigate.
 * @param {string} sessionUrl
 */
export function openStripeCheckout(sessionUrl) {
  if (!sessionUrl) throw new Error("Missing sessionUrl");
  window.location.assign(sessionUrl);
}

/**
 * Advanced: override base at runtime (no-op in this build).
 * Prefer VITE_FUNCTIONS_BASE_URL for env-based control.
 * @param {string} _newBase
 */
export function setStripeRpcBase(_newBase) {
  console.warn("setStripeRpcBase is a no-op in this build. Prefer VITE_FUNCTIONS_BASE_URL.");
}
